import { LitElement, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Styles from './vm-popover.scss';

/**
 * A popover component that displays content next to a trigger element.
 * Automatically adjusts horizontal and vertical alignment based on
 * available viewport space. Closes on outside click or when a
 * vm-popover-close event is dispatched from its content.
 *
 * @slot trigger - The element that toggles the popover on click.
 * @slot - Default slot for popover content (e.g. vm-dropdown, forms, info cards).
 *
 * @example
 * ```html
 * <vm-popover>
 *   <vm-button slot="trigger">Open</vm-button>
 *   <vm-dropdown>
 *     <vm-dropdown-item>
 *       <vm-icon-next slot="icon-leading" name="arrow-narrow-right"></vm-icon-next>
 *       Create new playlist
 *     </vm-dropdown-item>
 *   </vm-dropdown>
 * </vm-popover>
 * ```
 */
@customElement('vm-popover')
export class VmPopover extends LitElement {
    static override styles = Styles;

    /** Whether the popover panel is open. */
    @property({ type: Boolean, reflect: true })
    open: boolean = false;

    /** Horizontal alignment of the panel relative to the trigger. */
    @property({ reflect: true, attribute: 'align-horizontal' })
    alignHorizontal: 'left' | 'right' = 'left';

    /** Vertical alignment of the panel relative to the trigger. */
    @property({ reflect: true, attribute: 'align-vertical' })
    alignVertical: 'bottom' | 'top' = 'bottom';

    /**
     * Optional override for the alignment boundary. When unset, the popover
     * auto-detects its nearest scrollable ancestor (see `_findScrollBoundary`) and
     * falls back to the viewport — so a popover inside a modal body flips against the
     * modal without any wiring. Set this only to force a specific boundary; a detached
     * element is ignored (falls back to auto-detection / viewport).
     */
    @property({ attribute: false })
    boundary?: HTMLElement;

    private _alignmentFrame: number | null = null;

    override connectedCallback() {
        super.connectedCallback();
        this.classList.add('vm-popover');
        this.dataset.testid = 'vm-popover';
        this.addEventListener('click', this._onHostClick);
        this.addEventListener('vm-popover-close', this._onPopoverClose);
        document.addEventListener('click', this._onDocumentClick);
        document.addEventListener('keydown', this._onKeyDown);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this._onHostClick);
        this.removeEventListener('vm-popover-close', this._onPopoverClose);
        document.removeEventListener('click', this._onDocumentClick);
        document.removeEventListener('keydown', this._onKeyDown);
        this._removeViewportListeners();
    }

    override updated(changed: Map<string, unknown>) {
        if (changed.has('open')) {
            this._getTriggerElements().forEach((el) => {
                el.setAttribute('aria-expanded', String(this.open));
            });

            if (!this.open) {
                this._removeViewportListeners();
            }
        }
        // Recompute alignment if the boundary reference changes while open — the existing
        // capture-phase window scroll listener already catches the boundary's scroll events,
        // so no per-boundary listener wiring is needed.
        if (changed.has('boundary') && this.open) {
            this._scheduleAlignmentUpdate();
        }
    }

    /** Returns the light-DOM elements assigned to the trigger slot. */
    private _getTriggerElements(): Element[] {
        const slot = this.renderRoot.querySelector('slot[name="trigger"]') as HTMLSlotElement | null;
        return slot?.assignedElements() ?? [];
    }

    /** Applies ARIA attributes to the slotted trigger elements. */
    private _onTriggerSlotChange = () => {
        this._getTriggerElements().forEach((el) => {
            el.setAttribute('aria-haspopup', 'true');
            el.setAttribute('aria-expanded', String(this.open));
        });
    };

    /** Toggles the popover when a click originates from a slotted trigger element. */
    private _onHostClick = (event: MouseEvent) => {
        const trigger = event.composedPath().find((el) => (el as HTMLElement).slot === 'trigger');
        if (trigger) this.toggle();
    };

    /** Closes the popover when clicking outside of it. */
    private _onDocumentClick = (event: MouseEvent) => {
        if (this.open && !event.composedPath().includes(this)) {
            this.open = false;
        }
    };

    /** Closes the popover on Escape key. */
    private _onKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && this.open) {
            this.open = false;
        }
    };

    /** Closes the popover when content dispatches a vm-popover-close event. */
    private _onPopoverClose = () => {
        this.open = false;
    };

    private _addViewportListeners() {
        window.addEventListener('resize', this._scheduleAlignmentUpdate);
        // Capture-phase catches scrolls from any scrollable element on the page,
        // including a custom `boundary` if set — no per-boundary listener needed.
        window.addEventListener('scroll', this._scheduleAlignmentUpdate, true);
    }

    private _removeViewportListeners() {
        window.removeEventListener('resize', this._scheduleAlignmentUpdate);
        window.removeEventListener('scroll', this._scheduleAlignmentUpdate, true);
        if (this._alignmentFrame !== null) {
            cancelAnimationFrame(this._alignmentFrame);
            this._alignmentFrame = null;
        }
    }

    /** Throttles alignment updates to once per animation frame. */
    private _scheduleAlignmentUpdate = () => {
        if (this._alignmentFrame !== null) return;
        this._alignmentFrame = requestAnimationFrame(() => {
            this._alignmentFrame = null;
            this._updateAlignment();
        });
    };

    /**
     * Finds the nearest clipping ancestor in the flat tree (following slot assignments
     * and crossing shadow boundaries), to use as the alignment boundary. A clipping
     * ancestor is any element whose `overflow-y` isn't `visible` — i.e. `auto`/`scroll`
     * (scrolls the panel out of view) or `hidden`/`clip` (cuts it off). Its visible
     * rect is what the panel must fit inside. Returns null when nothing clips — the
     * caller falls back to the viewport. This is why a popover inside a modal body
     * needs no explicit `boundary`: the modal body is the nearest such ancestor.
     */
    private _findScrollBoundary(): HTMLElement | null {
        let node: Node | null = this.parentNode instanceof ShadowRoot ? this.parentNode.host : this.parentNode;
        while (node) {
            if (node instanceof HTMLElement && getComputedStyle(node).overflowY !== 'visible') {
                return node;
            }
            const el = node as Element;
            if (el.assignedSlot) node = el.assignedSlot;
            else if (node.parentNode instanceof ShadowRoot) node = node.parentNode.host;
            else node = node.parentNode;
        }
        return null;
    }

    /** Recalculates panel alignment based on available space (viewport or boundary). */
    private _updateAlignment = () => {
        const panel = this.renderRoot.querySelector('.vm-popover__panel') as HTMLElement | null;
        const trigger = this._getTriggerElements()[0] as HTMLElement | undefined;
        if (!panel || !trigger) return;

        const VIEWPORT_EDGE_BUFFER = 20;

        // Clear any prior shift so width measurements reflect the trigger-anchored position.
        panel.style.transform = '';

        const triggerRect = trigger.getBoundingClientRect();
        const panelWidth = panel.offsetWidth;
        const panelHeight = panel.offsetHeight;

        // Prefer an explicit `boundary`, else auto-detect the nearest scrollable
        // ancestor, else the viewport. A detached explicit boundary returns an
        // all-zero rect, so guard with `isConnected`.
        const boundary = this.boundary && this.boundary.isConnected ? this.boundary : this._findScrollBoundary();
        const bounds = boundary
            ? boundary.getBoundingClientRect()
            : { left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight };

        const spaceRight = bounds.right - triggerRect.left;
        this.alignHorizontal = spaceRight >= panelWidth + VIEWPORT_EDGE_BUFFER ? 'left' : 'right';

        const spaceBelow = bounds.bottom - triggerRect.bottom;
        this.alignVertical = spaceBelow >= panelHeight + VIEWPORT_EDGE_BUFFER ? 'bottom' : 'top';

        // Predict the panel's post-alignment position (attribute reflection is async, so we
        // can't measure it directly here) and translate it back inside if either edge overflows.
        const predictedLeft = this.alignHorizontal === 'left' ? triggerRect.left : triggerRect.right - panelWidth;
        const predictedRight = predictedLeft + panelWidth;

        let shiftX = 0;
        if (predictedLeft < VIEWPORT_EDGE_BUFFER) {
            shiftX = VIEWPORT_EDGE_BUFFER - predictedLeft;
        } else if (predictedRight > window.innerWidth - VIEWPORT_EDGE_BUFFER) {
            shiftX = window.innerWidth - VIEWPORT_EDGE_BUFFER - predictedRight;
        }
        if (shiftX !== 0) {
            panel.style.transform = `translateX(${shiftX}px)`;
        }
    };

    /** Toggles the popover open/closed and manages viewport listeners. */
    toggle() {
        this.open = !this.open;
        if (this.open) {
            this.updateComplete.then(() => this._updateAlignment());
            this._addViewportListeners();
        }
    }

    override render() {
        return html`
            <slot name="trigger" @slotchange=${this._onTriggerSlotChange}></slot>
            ${this.open
                ? html`<div class="vm-popover__panel" data-testid="vm-popover-panel">
                      <slot></slot>
                  </div>`
                : nothing}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-popover': VmPopover;
    }
}
